import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import AgreementAcceptanceDialog, {
	type AgreementMeta,
} from '@/components/legal/AgreementAcceptanceDialog';
import { Button } from '@/components/ui/button';
import type { RootState } from '@/reducers';

// Owners and location admins can accept business-wide agreements on behalf of
// the business (mirrors the add-voice billing route and Voice settings page).
const VOICE_AGREEMENT_ACCEPT_LEVELS = ['owner', 'location-admin'];

// Calm, non-blocking reminder that the business still needs to accept the Voice
// agreements (e.g. when support added Voice during registration). Shown on the
// dialer while the user types the number — never during an active call. Only
// owners/location admins can accept; other roles are told who needs to.
export function VoiceAgreementBanner() {
	const auth = useSelector((state: RootState) => state.auth);
	const hasVoiceProduct = !!auth?.products?.includes('voice');
	const canAccept = VOICE_AGREEMENT_ACCEPT_LEVELS.includes(
		auth?.user?.accessLevel || '',
	);

	const [pendingAgreements, setPendingAgreements] = useState<AgreementMeta[]>(
		[],
	);
	const [dialogOpen, setDialogOpen] = useState(false);

	const getPendingAgreements = useCallback(async () => {
		try {
			const res = await axios.get('/api/legal/missing-agreements', {
				params: { bundle: 'voice_add' },
			});
			setPendingAgreements(res.data?.missingAgreements || []);
		} catch {
			setPendingAgreements([]);
		}
	}, []);

	useEffect(() => {
		if (hasVoiceProduct) getPendingAgreements();
	}, [hasVoiceProduct, getPendingAgreements]);

	if (!hasVoiceProduct || pendingAgreements.length === 0) return null;

	return (
		<>
			<div className="space-y-0.5 text-[11px] leading-tight text-muted-foreground">
				<p>
					{canAccept
						? 'Please review and accept the Voice agreements to finish setup.'
						: 'An owner or location admin needs to accept the Voice agreements to finish setup.'}
				</p>
				{canAccept && (
					<Button
						variant="link"
						className="h-auto p-0 text-[11px] font-medium text-foreground"
						onClick={() => setDialogOpen(true)}
					>
						Review & accept
					</Button>
				)}
			</div>

			<AgreementAcceptanceDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				agreements={pendingAgreements}
				source="voice_dialer_reminder"
				title="Accept your Chekkit Voice agreements"
				description="Chekkit Voice was added to your account. To finish setup, please review and accept the Data Processing Terms and Recording & Compliance Terms."
				onAccepted={async () => {
					await getPendingAgreements();
					toast.success('Voice agreements accepted');
				}}
			/>
		</>
	);
}

export default VoiceAgreementBanner;
